import type { PointToPoint, Ratebook } from '@api/useFreight/types'
import { useRateManagement } from '@api/useFreight/useFreight'
import { useRef, useState, useCallback } from 'react'

/**
 * Custom hook for managing point-to-point data
 * @param ratebook The current ratebook
 * @returns Object containing point-to-point data and operations
 */
export function usePointToPointData(ratebook: Ratebook) {
  const { usePointToPointsQuery, exportPointToPointRates, importPointToPointRates } = useRateManagement()

  const gridRef = useRef<any>(null)
  const [selectedPointToPoint, setSelectedPointToPoint] = useState<PointToPoint | null>(null)
  const [editingPointToPoint, setEditingPointToPoint] = useState<PointToPoint | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false)

  // Fetch point-to-point data
  const { data: pointToPoints = [], isLoading, refetch } = usePointToPointsQuery(ratebook)

  /**
   * Open create drawer
   */
  const openCreateDrawer = useCallback(() => {
    setEditingPointToPoint(null)
    setDrawerVisible(true)
  }, [])

  /**
   * Open edit drawer
   */
  const openEditDrawer = useCallback((pointToPoint: PointToPoint) => {
    setEditingPointToPoint(pointToPoint)
    setDrawerVisible(true)
  }, [])

  /**
   * Open rate history drawer
   */
  const openRateHistoryDrawer = useCallback((pointToPoint: PointToPoint) => {
    setSelectedPointToPoint(pointToPoint)
    setHistoryDrawerVisible(true)
  }, [])

  /**
   * Close all drawers
   */
  const closeDrawers = useCallback(() => {
    setDrawerVisible(false)
    setHistoryDrawerVisible(false)
    setEditingPointToPoint(null)
  }, [])

  /**
   * Close rate history drawer
   */
  const closeRateHistoryDrawer = useCallback(() => {
    setHistoryDrawerVisible(false)
  }, [])

  /**
   * Handle successful point-to-point creation
   */
  const handlePointToPointSuccess = useCallback(
    (pointToPoint: PointToPoint) => {
      setSelectedPointToPoint(pointToPoint)
      refetch()
    },
    [refetch]
  )

  return {
    pointToPoints,
    isLoading,
    refetch,
    gridRef,
    selectedPointToPoint,
    editingPointToPoint,
    drawerVisible,
    historyDrawerVisible,
    openCreateDrawer,
    openEditDrawer,
    openRateHistoryDrawer,
    closeDrawers,
    closeRateHistoryDrawer,
    handlePointToPointSuccess,
    exportPointToPointRates,
    importPointToPointRates,
  }
}
